import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  PartnerProfile,
  clearPartnerToken,
  fetchPartnerMe,
  getPartnerToken,
  loginPartner,
  setPartnerToken,
  updatePartnerProfile,
} from '../services/partnerPortal';

interface PartnerAuthContextValue {
  partner: PartnerProfile | null;
  /** True until the stored token has been checked against the server. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  saveProfile: (update: Partial<PartnerProfile>) => Promise<void>;
  refresh: () => Promise<void>;
}

const PartnerAuthContext = createContext<PartnerAuthContextValue | undefined>(
  undefined
);

/**
 * Session for the external partner portal. Entirely separate from the admin
 * AuthContext — a partner and a staff member can be signed in at once on the
 * same browser without either evicting the other.
 */
export const PartnerAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [partner, setPartner] = useState<PartnerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // A stored token can be expired, or belong to an account that has since been
  // suspended, so it is only trusted once /me confirms it.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      if (!getPartnerToken()) {
        setLoading(false);
        return;
      }
      try {
        const me = await fetchPartnerMe();
        if (!cancelled) setPartner(me);
      } catch {
        clearPartnerToken();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { token, partner: signedIn } = await loginPartner(email, password);
    setPartnerToken(token);
    setPartner(signedIn);
  }, []);

  const signOut = useCallback(() => {
    clearPartnerToken();
    setPartner(null);
  }, []);

  const saveProfile = useCallback(async (update: Partial<PartnerProfile>) => {
    setPartner(await updatePartnerProfile(update));
  }, []);

  const refresh = useCallback(async () => {
    try {
      setPartner(await fetchPartnerMe());
    } catch {
      clearPartnerToken();
      setPartner(null);
    }
  }, []);

  const value = useMemo(
    () => ({ partner, loading, signIn, signOut, saveProfile, refresh }),
    [partner, loading, signIn, signOut, saveProfile, refresh]
  );

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
};

export const usePartnerAuth = (): PartnerAuthContextValue => {
  const context = useContext(PartnerAuthContext);
  if (!context) {
    throw new Error('usePartnerAuth must be used inside a PartnerAuthProvider');
  }
  return context;
};
